import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { propertiesAPI } from '../services/api';
import { ChevronLeft, Home as HomeIcon, Plus, X, Upload, ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import RoomsEditor from '../components/RoomsEditor';
import SustainabilityChecklist, { validateSustainability } from '../components/SustainabilityChecklist';

const CATEGORIES = [
  'Mountains',
  'Beachfront',
  'Rural Homestay',
  'Lakefront',
  'Tropical',
  'Farms',
  'Historic homes'
];

const AMENITIES = [
  'WiFi',
  'Kitchen',
  'Free parking',
  'Air conditioning',
  'Heating',
  'Pool',
  'Beach access',
  'Mountain view',
  'Ocean view',
  'Lake view',
  'Fireplace',
  'BBQ grill',
  'Washer',
  'Pet friendly',
  'Garden'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SimplifiedPropertyRegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [propertyImages, setPropertyImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // PAN gate state
  const [panChecking, setPanChecking] = useState(true);
  // Is this session an admin acting as the GeoStays system host? (Company is
  // pre-verified so KYC & bank evidence uploads are hidden.)
  const isSystemHost =
    typeof window !== 'undefined' && localStorage.getItem('adminActingAsHost') === '1';
  const [panVerified, setPanVerified] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    country: 'India',
    guests: 2,
    bedrooms: 1,
    beds: 1,
    bathrooms: 1,
    price: '',
    listing_type: 'entire', // 'entire' | 'rooms'
    rooms: [], // list of room objects when listing_type='rooms'
    category: '',
    amenities: [],
    description: '',
    hostName: '',
    hostEmail: '',
    hostPhone: '',
    hostImage: '',
    // KYC Files
    hostPhoto: null,
    aadhaarCard: null,
    panCard: null,
    bankCheque: null,
    // PAN Details
    panNumber: '',
    panHolderName: '',
    // Bank Details
    accountName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    sustainability: { answers: {}, proof_photos: {} },
  });

  // Helpers for managing rooms array
  const addRoom = () => {
    setFormData((prev) => ({
      ...prev,
      rooms: [
        ...prev.rooms,
        { category: '', beds: 1, bathrooms: 1, max_guests: 2, price: '', inventory: 1, amenities: [], description: '' },
      ],
    }));
  };

  const updateRoom = (idx, field, value) => {
    setFormData((prev) => {
      const next = [...prev.rooms];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, rooms: next };
    });
  };

  const removeRoom = (idx) => {
    setFormData((prev) => ({ ...prev, rooms: prev.rooms.filter((_, i) => i !== idx) }));
  };

  // Auto-fetch host details from registration so KYC section is prefilled
  useEffect(() => {
    let cancelled = false;
    const fetchHost = async () => {
      const hostId = localStorage.getItem('hostId');
      if (!hostId) {
        if (!cancelled) {
          setPanChecking(false);
          toast.error('Please login as a host first.');
          navigate('/host/login');
        }
        return;
      }
      try {
        // Check PAN verification status first
        const vRes = await axios.get(`${BACKEND_URL}/api/host/verification-status/${hostId}`);
        if (!cancelled && vRes.data?.success) {
          const data = vRes.data.data || {};
          const isVerified = !!data.verified;
          setPanVerified(isVerified);
          // Auto-populate PAN + name from the KYC record so hosts don't retype
          // and can't accidentally submit a different PAN than the verified one.
          if (isVerified) {
            setFormData(prev => ({
              ...prev,
              panNumber: prev.panNumber || data.pan_number || '',
              panHolderName: prev.panHolderName || data.verified_name || '',
            }));
          }
        }
      } catch (e) {
        console.warn('Could not fetch PAN verification status:', e);
      } finally {
        if (!cancelled) setPanChecking(false);
      }

      try {
        const res = await axios.get(`${BACKEND_URL}/api/host/dashboard/${hostId}`);
        if (!cancelled && res.data?.success && res.data.host) {
          const h = res.data.host;
          const cleanPhone = (h.phone || '').replace(/\D/g, '').slice(-10);
          setFormData(prev => ({
            ...prev,
            hostName: prev.hostName || h.name || '',
            hostEmail: prev.hostEmail || h.email || '',
            hostPhone: prev.hostPhone || cleanPhone,
            // Company host has a fixed avatar; regular hosts leave this blank
            // and fall back to ui-avatars.com on the backend.
            hostImage: prev.hostImage || h.host_image_url || h.avatar || '',
          }));
        }
      } catch (e) {
        console.warn('Failed to auto-fetch host details for KYC prefill:', e);
      }

      // Admin acting as the GeoStays company host: auto-populate the company bank
      // details so no further evidences are needed. These match the verified
      // corporate account and are locked to prevent accidental edits.
      if (!cancelled && localStorage.getItem('adminActingAsHost') === '1') {
        setFormData(prev => ({
          ...prev,
          accountName: 'Geostays Hospitality Private Limited',
          accountNumber: '492305000035',
          ifscCode: 'ICIC0004923',
          bankName: 'ICICI Bank, Lancohills',
        }));
      }
    };
    fetchHost();
    return () => { cancelled = true; };
  }, [navigate]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    const newAmenities = formData.amenities.includes(amenity)
      ? formData.amenities.filter(a => a !== amenity)
      : [...formData.amenities, amenity];
    updateFormData('amenities', newAmenities);
  };

  const handlePropertyImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (propertyImages.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }
    
    // Add new files
    setPropertyImages(prev => [...prev, ...files]);
    
    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePropertyImage = (index) => {
    setPropertyImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // IFSC Code validation - Format: 4 letters + 0 + 6 alphanumeric characters
  const validateIFSC = (ifsc) => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  // PAN Number validation - Format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
  const validatePAN = (pan) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan.toUpperCase());
  };

  // Auto-format IFSC code to uppercase
  const handleIFSCChange = (value) => {
    const formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    updateFormData('ifscCode', formattedValue);
  };

  // Auto-format PAN number to uppercase
  const handlePANChange = (value) => {
    const formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    updateFormData('panNumber', formattedValue);
  };

  const validateForm = () => {
    if (!formData.title) {
      toast.error('Please enter property title');
      return false;
    }
    if (!formData.location) {
      toast.error('Please enter location');
      return false;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return false;
    }
    if (formData.listing_type === 'entire') {
      if (!formData.price || formData.price <= 0) {
        toast.error('Please enter a valid price per night for the entire property');
        return false;
      }
    } else {
      // Per-room validation
      if (!formData.rooms.length) {
        toast.error('Please add at least one room type');
        return false;
      }
      for (let i = 0; i < formData.rooms.length; i++) {
        const r = formData.rooms[i];
        if (!r.category?.trim()) { toast.error(`Room #${i + 1}: enter a category (e.g., Deluxe Room)`); return false; }
        if (!r.price || Number(r.price) <= 0) { toast.error(`Room #${i + 1}: enter a valid price`); return false; }
        if (!r.inventory || Number(r.inventory) < 1) { toast.error(`Room #${i + 1}: inventory must be at least 1`); return false; }
      }
    }
    if (!formData.description) {
      toast.error('Please add a description');
      return false;
    }
    if (propertyImages.length < 1) {
      toast.error('Please upload at least one property photo');
      return false;
    }
    if (!formData.hostName) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.hostEmail) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.hostPhone) {
      toast.error('Please enter your phone number');
      return false;
    }
    const cleanPhone = (formData.hostPhone || '').trim();
    if (!/^\d{10}$/.test(cleanPhone)) {
      toast.error('Mobile number must be exactly 10 digits');
      return false;
    }
    // Admin acting as the GeoStays system host bypasses all KYC + bank evidence
    // uploads (company is already verified).

    // KYC Document validation
    if (!isSystemHost && !formData.hostPhoto) {
      toast.error('Please upload your photo');
      return false;
    }
    if (!isSystemHost && !formData.aadhaarCard) {
      toast.error('Please upload your Aadhaar Card');
      return false;
    }
    if (!isSystemHost && !formData.panCard) {
      toast.error('Please upload your PAN Card');
      return false;
    }
    if (!formData.panNumber) {
      toast.error('Please enter your PAN number');
      return false;
    }
    if (!validatePAN(formData.panNumber)) {
      toast.error('Invalid PAN number format. It should be 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)');
      return false;
    }
    if (!formData.panHolderName) {
      toast.error('Please enter PAN card holder name');
      return false;
    }
    if (!isSystemHost && !formData.bankCheque) {
      toast.error('Please upload your Cancelled Cheque / Account Statement / Passbook');
      return false;
    }
    // Bank details validation
    if (!formData.accountName) {
      toast.error('Please enter account holder name');
      return false;
    }
    if (!formData.accountNumber) {
      toast.error('Please enter bank account number');
      return false;
    }
    if (!formData.ifscCode) {
      toast.error('Please enter IFSC code');
      return false;
    }
    if (!validateIFSC(formData.ifscCode)) {
      toast.error('Invalid IFSC code format. It should be 4 letters + 0 + 6 alphanumeric characters (e.g., SBIN0001234)');
      return false;
    }
    if (!formData.bankName) {
      toast.error('Please enter bank name');
      return false;
    }

    // Sustainability: every checked item must have a proof photo
    const sustError = validateSustainability(formData.sustainability);
    if (sustError) {
      toast.error(sustError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!panVerified) {
      toast.error('Please verify your PAN before submitting a property');
      return;
    }
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      // Get host_id from localStorage (if verified host)
      const hostId = localStorage.getItem('hostId') || '';
      
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('type', formData.listing_type === 'rooms' ? 'Private rooms' : 'Entire home');
      submitData.append('location', formData.location);
      submitData.append('country', formData.country);
      submitData.append('guests', formData.guests);
      submitData.append('bedrooms', formData.bedrooms);
      submitData.append('beds', formData.beds);
      submitData.append('bathrooms', formData.bathrooms);
      submitData.append('price', formData.listing_type === 'rooms'
        ? Math.min(...formData.rooms.map(r => Number(r.price) || 0))
        : formData.price);
      submitData.append('listing_type', formData.listing_type);
      submitData.append('rooms_json', formData.listing_type === 'rooms'
        ? JSON.stringify(formData.rooms.map(r => ({
            category: r.category,
            beds: Number(r.beds) || 1,
            bathrooms: Number(r.bathrooms) || 1,
            max_guests: Number(r.max_guests) || 2,
            price: Number(r.price) || 0,
            inventory: Number(r.inventory) || 1,
            amenities: r.amenities || [],
            description: r.description || '',
          })))
        : '');
      submitData.append('sustainability_json', JSON.stringify(formData.sustainability || { answers: {}, proof_photos: {} }));
      submitData.append('images', ''); // Empty, using file uploads instead
      submitData.append('category', formData.category);
      submitData.append('amenities', formData.amenities.join(','));
      submitData.append('description', formData.description);
      submitData.append('hostName', formData.hostName);
      submitData.append('hostEmail', formData.hostEmail);
      submitData.append('hostPhone', formData.hostPhone);
      submitData.append('hostImage', formData.hostImage || '');
      submitData.append('host_id', hostId);
      
      // Bank details
      submitData.append('accountName', formData.accountName);
      submitData.append('accountNumber', formData.accountNumber);
      submitData.append('ifscCode', formData.ifscCode);
      submitData.append('bankName', formData.bankName);
      
      // PAN Details
      submitData.append('panNumber', formData.panNumber);
      submitData.append('panHolderName', formData.panHolderName);
      
      // KYC Files
      if (formData.hostPhoto) submitData.append('hostPhoto', formData.hostPhoto);
      if (formData.aadhaarCard) submitData.append('aadhaarCard', formData.aadhaarCard);
      if (formData.panCard) submitData.append('panCard', formData.panCard);
      if (formData.bankCheque) submitData.append('bankCheque', formData.bankCheque);
      
      // Property Images (file uploads)
      propertyImages.forEach((file) => {
        submitData.append('propertyImages', file);
      });

      const response = await propertiesAPI.createWithKYC(submitData);
      
      if (response.success) {
        toast.success('Homestay submitted successfully! It will be published after admin approval.');
        setTimeout(() => {
          // Redirect to dashboard if host, otherwise home
          if (hostId) {
            navigate('/host/dashboard');
          } else {
            navigate('/');
          }
        }, 2500);
      }
    } catch (error) {
      console.error('Error submitting property:', error);
      toast.error('Failed to submit homestay. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to home
          </Button>
          <div className="flex items-center space-x-4 mb-2">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center shadow-lg">
              <HomeIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Register Your Homestay</h1>
              <p className="text-gray-600 mt-1">Share your space with travelers from around the world</p>
            </div>
          </div>
        </div>

        {/* PAN Verification Gate */}
        {panChecking ? (
          <Card className="mb-8" data-testid="pan-gate-loading">
            <CardContent className="py-10 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#7CB342]" />
              <span className="ml-3 text-sm text-gray-500">Checking host verification...</span>
            </CardContent>
          </Card>
        ) : !panVerified ? (
          <Card className="mb-8 bg-orange-50 border-orange-200" data-testid="pan-gate-blocked">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-4">
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-orange-900 mb-1">PAN verification required</h3>
                  <p className="text-sm text-orange-800 mb-4">
                    For trust and compliance, every host must verify their PAN against Income Tax Department
                    records (via Sandbox.co.in) before listing a homestay. It takes less than a minute and your
                    PAN is encrypted at rest.
                  </p>
                  <Button
                    onClick={() => navigate('/host/dashboard')}
                    className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                    data-testid="pan-gate-verify-cta"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Verify PAN now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Info Card */}
        <Card className="mb-8 bg-green-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <div className="h-6 w-6 rounded-full bg-[#7CB342] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">!</span>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  <strong>Review Process:</strong> Your homestay will be reviewed by our team before being published. 
                  This typically takes 24-48 hours. We&apos;ll notify you via email once approved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Form */}
        <form onSubmit={handleSubmit}>
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle>Homestay Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center mr-3 text-sm">1</span>
                  Basic Information
                </h3>
                
                <div>
                  <Label htmlFor="title">Property Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Cozy Mountain Homestay with Garden View"
                    value={formData.title}
                    onChange={(e) => updateFormData('title', e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Location (City, State) *</Label>
                    <Input
                      id="location"
                      placeholder="e.g., Manali, Himachal Pradesh"
                      value={formData.location}
                      onChange={(e) => updateFormData('location', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => updateFormData('country', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => updateFormData('category', value)}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Property Specifications */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center mr-3 text-sm">2</span>
                  Property Specifications
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="guests">Guests</Label>
                    <Input
                      id="guests"
                      type="number"
                      min="1"
                      value={formData.guests}
                      onChange={(e) => updateFormData('guests', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      min="1"
                      value={formData.bedrooms}
                      onChange={(e) => updateFormData('bedrooms', e.target.value)}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="beds">Beds</Label>
                    <Input
                      id="beds"
                      type="number"
                      min="1"
                      value={formData.beds}
                      onChange={(e) => updateFormData('beds', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      min="1"
                      value={formData.bathrooms}
                      onChange={(e) => updateFormData('bathrooms', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                {/* Listing type toggle */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <Label className="text-base font-semibold mb-3 block">What are you listing? *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => updateFormData('listing_type', 'entire')}
                      className={`text-left p-4 rounded-lg border-2 transition-all ${
                        formData.listing_type === 'entire'
                          ? 'border-[#7CB342] bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid="listing-type-entire"
                    >
                      <div className="font-semibold text-sm mb-1">Entire Property</div>
                      <div className="text-xs text-gray-500">Guests book the whole place (one price)</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateFormData('listing_type', 'rooms')}
                      className={`text-left p-4 rounded-lg border-2 transition-all ${
                        formData.listing_type === 'rooms'
                          ? 'border-[#7CB342] bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid="listing-type-rooms"
                    >
                      <div className="font-semibold text-sm mb-1">Individual Rooms</div>
                      <div className="text-xs text-gray-500">Each room type has its own price & inventory</div>
                    </button>
                  </div>
                </div>

                {formData.listing_type === 'entire' ? (
                  <div>
                    <Label htmlFor="price">Price per Night (₹) *</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      placeholder="e.g., 3500"
                      value={formData.price}
                      onChange={(e) => updateFormData('price', e.target.value)}
                      className="mt-2"
                      data-testid="property-price-input"
                    />
                  </div>
                ) : (
                  <RoomsEditor
                    rooms={formData.rooms}
                    onChange={(next) => updateFormData('rooms', next)}
                    testIdPrefix="room"
                  />
                )}
              </div>

              {/* Amenities */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center mr-3 text-sm">3</span>
                  Amenities
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {AMENITIES.map(amenity => (
                    <div key={amenity} className="flex items-center space-x-2">
                      <Checkbox
                        id={amenity}
                        checked={formData.amenities.includes(amenity)}
                        onCheckedChange={() => toggleAmenity(amenity)}
                      />
                      <label
                        htmlFor={amenity}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {amenity}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sustainability Self-Assessment */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center mr-3 text-sm">🌿</span>
                  Sustainability (optional)
                </h3>
                <SustainabilityChecklist
                  value={formData.sustainability}
                  onChange={(next) => updateFormData('sustainability', next)}
                />
              </div>

              {/* Description */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center mr-3 text-sm">4</span>
                  Description & Photos
                </h3>
                
                <div>
                  <Label htmlFor="description">Describe your homestay *</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell guests about your homestay, what makes it special, nearby attractions, house rules, etc."
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    className="mt-2 min-h-32"
                  />
                </div>

                <div>
                  <Label>Property Photos * (Upload up to 10 photos)</Label>
                  <p className="text-sm text-gray-500 mb-3">Add high-quality photos of your homestay (JPG, PNG, max 5MB each)</p>
                  
                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {imagePreviews.map((preview, index) => (
                        <div key={preview} className="relative group">
                          <img
                            src={preview}
                            alt={`Property ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removePropertyImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          {index === 0 && (
                            <span className="absolute bottom-1 left-1 text-xs bg-[#7CB342] text-white px-2 py-0.5 rounded">
                              Cover
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  {propertyImages.length < 10 && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#7CB342] transition-colors">
                      <input
                        type="file"
                        id="propertyImages"
                        multiple
                        accept="image/*"
                        onChange={handlePropertyImageUpload}
                        className="hidden"
                      />
                      <label htmlFor="propertyImages" className="cursor-pointer">
                        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          Click to upload photos
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {propertyImages.length}/10 photos uploaded
                        </p>
                      </label>
                    </div>
                  )}
                  
                  {propertyImages.length >= 10 && (
                    <p className="text-sm text-green-600 mt-2">Maximum photos uploaded (10/10)</p>
                  )}
                </div>
              </div>

              {/* Host Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="h-8 w-8 rounded-full bg-[#7CB342] text-white flex items-center justify-center mr-3 text-sm">5</span>
                  Host Information & KYC Documents
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hostName">Your Name *</Label>
                    <Input
                      id="hostName"
                      placeholder="e.g., Rajesh Kumar"
                      value={formData.hostName}
                      onChange={(e) => updateFormData('hostName', e.target.value)}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="hostEmail">Email Address *</Label>
                    <Input
                      id="hostEmail"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.hostEmail}
                      onChange={(e) => updateFormData('hostEmail', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="hostPhone">Mobile Number *</Label>
                  <Input
                    id="hostPhone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="9876543210"
                    value={formData.hostPhone}
                    onChange={(e) => updateFormData('hostPhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">10-digit mobile number (digits only)</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-sm text-gray-900 mb-3 flex items-center">
                    <Upload className="h-4 w-4 mr-2 text-[#7CB342]" />
                    KYC Documents {isSystemHost ? '(Pre-verified for GeoStays company)' : '(All Mandatory)'}
                  </h4>
                  {isSystemHost ? (
                    <p className="text-xs text-emerald-700 mb-2" data-testid="system-host-kyc-skip">
                      ✓ KYC evidences already on file for <strong>Geostays Hospitality Private Limited</strong>. No further uploads needed for company-owned properties.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-600 mb-4">
                      Upload clear, readable copies of the following documents for verification
                    </p>
                  )}

                  <div className="space-y-4">
                    {/* Host Photo */}
                    {!isSystemHost && (
                    <div>
                      <Label htmlFor="hostPhoto">Your Photo * (Max 5MB)</Label>
                      <Input
                        id="hostPhoto"
                        type="file"
                        accept="image/*"
                        onChange={(e) => updateFormData('hostPhoto', e.target.files[0])}
                        className="mt-2"
                      />
                      {formData.hostPhoto && <p className="text-xs text-green-600 mt-1">✓ {formData.hostPhoto.name}</p>}
                      {!formData.hostPhoto && <p className="text-xs text-gray-500 mt-1">Upload a clear passport-size photo</p>}
                    </div>
                    )}

                    {/* Aadhaar Card */}
                    {!isSystemHost && (
                    <div>
                      <Label htmlFor="aadhaarCard">Aadhaar Card * (Max 5MB)</Label>
                      <Input
                        id="aadhaarCard"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => updateFormData('aadhaarCard', e.target.files[0])}
                        className="mt-2"
                      />
                      {formData.aadhaarCard && <p className="text-xs text-green-600 mt-1">✓ {formData.aadhaarCard.name}</p>}
                      {!formData.aadhaarCard && <p className="text-xs text-gray-500 mt-1">Upload front and back or both sides in one file</p>}
                    </div>
                    )}

                    {/* PAN Card */}
                    {!isSystemHost && (
                    <div>
                      <Label htmlFor="panCard">PAN Card * (Max 5MB)</Label>
                      <Input
                        id="panCard"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => updateFormData('panCard', e.target.files[0])}
                        className="mt-2"
                      />
                      {formData.panCard && <p className="text-xs text-green-600 mt-1">✓ {formData.panCard.name}</p>}
                      {!formData.panCard && <p className="text-xs text-gray-500 mt-1">Clear copy of PAN card</p>}
                    </div>
                    )}

                    {/* PAN Card Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="panNumber">
                          PAN Number *
                          {panVerified && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle"
                              data-testid="pan-locked-badge"
                            >
                              ✓ KYC-verified · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="panNumber"
                          placeholder="e.g., ABCDE1234F"
                          value={formData.panNumber}
                          onChange={(e) => handlePANChange(e.target.value)}
                          maxLength={10}
                          readOnly={panVerified}
                          disabled={panVerified}
                          className={`mt-2 uppercase ${panVerified ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''} ${formData.panNumber && !panVerified && !validatePAN(formData.panNumber) ? 'border-red-500' : ''}`}
                          data-testid="pan-number-input"
                        />
                        {panVerified ? (
                          <p className="text-xs text-emerald-700 mt-1">
                            Locked to the PAN verified during KYC. Contact support if this needs to change.
                          </p>
                        ) : (
                          <>
                            {formData.panNumber && !validatePAN(formData.panNumber) && (
                              <p className="text-xs text-red-500 mt-1">
                                Format: 5 letters + 4 digits + 1 letter (e.g., ABCDE1234F)
                              </p>
                            )}
                            {formData.panNumber && validatePAN(formData.panNumber) && (
                              <p className="text-xs text-green-600 mt-1">✓ Valid PAN format</p>
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="panHolderName">
                          PAN Card Holder Name *
                          {panVerified && formData.panHolderName && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle">
                              ✓ KYC-verified · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="panHolderName"
                          placeholder="Name as on PAN card"
                          value={formData.panHolderName}
                          onChange={(e) => updateFormData('panHolderName', e.target.value.toUpperCase())}
                          readOnly={panVerified && !!formData.panHolderName}
                          disabled={panVerified && !!formData.panHolderName}
                          className={`mt-2 uppercase ${panVerified && !!formData.panHolderName ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''}`}
                          data-testid="pan-holder-name-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {panVerified && formData.panHolderName
                            ? 'Auto-filled from your KYC record.'
                            : 'Enter name exactly as printed on PAN card'}
                        </p>
                      </div>
                    </div>

                    {/* Bank Cheque */}
                    {!isSystemHost && (
                    <div>
                      <Label htmlFor="bankCheque">Cancelled Cheque / Account Statement / Account Passbook * (Max 5MB)</Label>
                      <p className="text-xs text-gray-500 mb-2">Account Name, Account Number and IFSC code should be clearly visible</p>
                      <Input
                        id="bankCheque"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => updateFormData('bankCheque', e.target.files[0])}
                        className="mt-2"
                      />
                      {formData.bankCheque && <p className="text-xs text-green-600 mt-1">✓ {formData.bankCheque.name}</p>}
                      {!formData.bankCheque && <p className="text-xs text-gray-500 mt-1">Account name should match with PAN card. Booking amounts will be credited to this account.</p>}
                    </div>
                    )}

                    {isSystemHost && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="system-host-bank-notice">
                        ✓ Bank details on file for <strong>Geostays Hospitality Private Limited</strong>. No cancelled cheque or passbook required.
                      </div>
                    )}

                    {/* Bank Account Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div>
                        <Label htmlFor="accountName">
                          Account Holder Name *
                          {isSystemHost && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle">
                              ✓ Company · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="accountName"
                          placeholder="As per bank records"
                          value={formData.accountName}
                          onChange={(e) => updateFormData('accountName', e.target.value)}
                          readOnly={isSystemHost}
                          disabled={isSystemHost}
                          className={`mt-2 ${isSystemHost ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''}`}
                          data-testid="bank-account-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="accountNumber">
                          Account Number *
                          {isSystemHost && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle">
                              ✓ Company · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="accountNumber"
                          placeholder="Bank account number"
                          value={formData.accountNumber}
                          onChange={(e) => updateFormData('accountNumber', e.target.value)}
                          readOnly={isSystemHost}
                          disabled={isSystemHost}
                          className={`mt-2 ${isSystemHost ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''}`}
                          data-testid="bank-account-number"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ifscCode">
                          IFSC Code *
                          {isSystemHost && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle">
                              ✓ Company · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="ifscCode"
                          placeholder="e.g., SBIN0001234"
                          value={formData.ifscCode}
                          onChange={(e) => handleIFSCChange(e.target.value)}
                          maxLength={11}
                          readOnly={isSystemHost}
                          disabled={isSystemHost}
                          className={`mt-2 uppercase ${isSystemHost ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''} ${formData.ifscCode && !isSystemHost && !validateIFSC(formData.ifscCode) ? 'border-red-500' : ''}`}
                          data-testid="bank-ifsc"
                        />
                        {!isSystemHost && formData.ifscCode && !validateIFSC(formData.ifscCode) && (
                          <p className="text-xs text-red-500 mt-1">
                            Format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)
                          </p>
                        )}
                        {!isSystemHost && formData.ifscCode && validateIFSC(formData.ifscCode) && (
                          <p className="text-xs text-green-600 mt-1">✓ Valid IFSC format</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="bankName">
                          Bank Name *
                          {isSystemHost && (
                            <span className="ml-2 inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle">
                              ✓ Company · locked
                            </span>
                          )}
                        </Label>
                        <Input
                          id="bankName"
                          placeholder="e.g., State Bank of India"
                          value={formData.bankName}
                          onChange={(e) => updateFormData('bankName', e.target.value)}
                          readOnly={isSystemHost}
                          disabled={isSystemHost}
                          className={`mt-2 ${isSystemHost ? 'bg-gray-100 cursor-not-allowed text-gray-700' : ''}`}
                          data-testid="bank-name"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {!isSystemHost && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <p className="text-xs text-gray-700">
                    <strong>Important:</strong> All documents will be verified by our team. 
                    Ensure the account holder name on the bank document matches exactly with the name on your PAN card. 
                    Mismatched information will delay verification.
                  </p>
                </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t">
                <Button
                  type="submit"
                  disabled={loading || !panVerified}
                  className="w-full py-6 text-lg font-semibold bg-[#7CB342] hover:bg-[#6da036] text-white disabled:opacity-60"
                  data-testid="property-submit-btn"
                  title={!panVerified ? 'Verify your PAN first' : ''}
                >
                  {loading ? 'Submitting...' : !panVerified ? 'Verify PAN to submit' : 'Submit Homestay for Approval'}
                </Button>
                <p className="text-xs text-center text-gray-500 mt-3">
                  By submitting, you agree to our terms and conditions
                </p>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default SimplifiedPropertyRegistrationPage;
